import { useState } from "react";
import { Link } from "@tanstack/react-router";
import { Controller, useForm, type Resolver } from "react-hook-form";
import { z } from "zod";
import {
  Alert,
  Badge,
  Button,
  DataTable,
  InputText,
  Modal,
  SectionCard,
  Select,
  Tooltip,
  type ColumnDef,
} from "@/deps/ui-library";
import {
  useAddReportTag,
  useArchiveReportTag,
  useRenameReportTag,
  useReportFrameworks,
  useSubscriptionStatus,
} from "@/api/queries";
import type { AccountGroup, ReportFramework, ReportTag } from "@/api/types";
import { useCan } from "@/app/session";
import { EmptyState } from "@/components/EmptyState";
import { SegmentControl } from "@/components/SegmentControl";

/** The feature's code in RigoSub's catalogue (ADR-0061). */
const FEATURE = "REPORTING";

/** The side an account of this type normally sits on. */
const normalSide = (group: AccountGroup) =>
  group === "Assets" || group === "Expenses" ? "Debit" : "Credit";

const CATEGORY: ColumnDef<ReportTag> = {
  id: "category",
  header: "IFRS 18 category",
  size: 150,
  cell: ({ row: { original } }) =>
    original.category
      ? original.category.charAt(0).toUpperCase() + original.category.slice(1)
      : "—",
};

function columns(
  withCategory: boolean,
  onRename: ((tag: ReportTag) => void) | null,
  onArchive: ((tag: ReportTag) => void) | null,
) {
  const list: Array<ColumnDef<ReportTag>> = [
    {
      id: "code",
      header: "Code",
      size: 220,
      cell: ({ row: { original } }) => (
        <span className={original.parentCode ? "num text-foreground pl-4" : "num text-foreground"}>
          {original.code}
        </span>
      ),
    },
    {
      id: "label",
      header: "Tag",
      cell: ({ row: { original } }) => (
        <span className={original.parentCode ? "flex items-center gap-2 pl-4" : "text-foreground"}>
          <span className={original.archived ? "text-fg-muted" : "text-foreground"}>
            {original.label}
          </span>
          {original.parentCode ? (
            <Badge colorScheme={original.archived ? "default" : "info"} size="sm">
              {original.archived ? "Archived" : "Company"}
            </Badge>
          ) : null}
        </span>
      ),
    },
    { id: "group", header: "Account type", size: 130, cell: ({ row }) => row.original.group },
    {
      id: "side",
      header: "Normal side",
      size: 110,
      cell: ({ row }) => normalSide(row.original.group),
    },
  ];
  if (withCategory) list.push(CATEGORY);
  if (!onRename || !onArchive) return list;
  const actions: ColumnDef<ReportTag> = {
    id: "manage",
    header: "",
    size: 90,
    cell: ({ row: { original } }) =>
      original.parentCode && !original.archived ? (
        <span className="flex justify-end gap-1">
          <Tooltip content="Rename">
            <Button.IconButton
              icon="Pencil"
              iconVariant="cell"
              variant="secondary"
              size="xs"
              aria-label={`Rename ${original.code}`}
              onClick={() => onRename(original)}
            />
          </Tooltip>
          <Tooltip content="Archive">
            <Button.IconButton
              icon="Archive"
              iconVariant="cell"
              variant="secondary"
              size="xs"
              aria-label={`Archive ${original.code}`}
              onClick={() => onArchive(original)}
            />
          </Tooltip>
        </span>
      ) : null,
  };
  return [...list, actions];
}

const schema = z.object({
  parentCode: z.string().min(1, "Choose the supplied tag it comes under."),
  code: z
    .string()
    .trim()
    .transform((code) => code.toUpperCase())
    .pipe(
      z
        .string()
        .regex(
          /^[A-Z0-9][A-Z0-9._-]{0,39}$/,
          "Up to 40 capital letters, digits, dots, dashes or underscores.",
        ),
    ),
  label: z.string().trim().min(1, "Give the tag a name."),
});
type Values = z.input<typeof schema>;

/** The one check this form has, as react-hook-form asks for it: each problem on its own field. */
const resolver: Resolver<Values> = (values) => {
  const parsed = schema.safeParse(values);
  if (parsed.success) return { values: parsed.data, errors: {} };
  const errors: Record<string, { type: string; message: string }> = {};
  for (const issue of parsed.error.issues) {
    errors[String(issue.path[0])] ??= { type: issue.code, message: issue.message };
  }
  return { values: {}, errors };
};

/** Adds a company tag under one of the framework's supplied tags (ADR-0065). */
function AddTagDialog({ framework, onClose }: { framework: ReportFramework; onClose: () => void }) {
  const add = useAddReportTag();
  const supplied = framework.tags.filter((tag) => !tag.parentCode);
  const {
    control,
    handleSubmit,
    watch,
    formState: { errors },
  } = useForm<Values>({ resolver, defaultValues: { parentCode: "", code: "", label: "" } });
  const parent = supplied.find((tag) => tag.code === watch("parentCode"));

  return (
    <Modal.Form
      open
      title="Add a tag"
      subtitle={`A tag of your own in ${framework.name}, under one of its supplied tags. It takes that tag's account type and category, and a supplied format adds it up on that tag's line.`}
      submitLabel="Add tag"
      busy={add.isPending}
      error={add.error ? (add.error as Error).message : null}
      onClose={onClose}
      onSubmit={() =>
        void handleSubmit((values) =>
          add.mutate(
            {
              framework: framework.code,
              tag: { parentCode: values.parentCode, code: values.code, label: values.label },
            },
            { onSuccess: onClose },
          ),
        )()
      }
    >
      <div className="flex flex-col gap-3">
        <Controller
          control={control}
          name="parentCode"
          render={({ field }) => (
            <Select
              required
              searchable
              label="Comes under"
              placeholder="Choose a supplied tag"
              options={supplied.map((tag) => ({
                value: tag.code,
                label: `${tag.code} · ${tag.label}`,
              }))}
              value={field.value}
              errorText={errors.parentCode?.message}
              helperText={
                parent
                  ? `${parent.group}, ${normalSide(parent.group).toLowerCase()} side`
                  : undefined
              }
              onChange={(details) => {
                const picked = Array.isArray(details.value) ? details.value[0] : details.value;
                field.onChange(picked ? String(picked.value) : "");
              }}
            />
          )}
        />
        <Controller
          control={control}
          name="code"
          render={({ field }) => (
            <InputText
              required
              label="Code"
              placeholder={parent ? `${parent.code}.YOURS` : "EXP.STAFF.WELFARE"}
              helperText="Your own, not one of the framework's. It can't be changed later."
              value={field.value}
              errorText={errors.code?.message}
              onChange={(event) => field.onChange(event.target.value.toUpperCase())}
            />
          )}
        />
        <Controller
          control={control}
          name="label"
          render={({ field }) => (
            <InputText
              required
              label="Name"
              placeholder="Staff welfare"
              value={field.value}
              errorText={errors.label?.message}
              onChange={(event) => field.onChange(event.target.value)}
            />
          )}
        />
      </div>
    </Modal.Form>
  );
}

/** Renames a company tag; its code stays, so mappings and formats keep it. */
function RenameTagDialog({
  framework,
  tag,
  onClose,
}: {
  framework: string;
  tag: ReportTag;
  onClose: () => void;
}) {
  const rename = useRenameReportTag();
  const [label, setLabel] = useState(tag.label);
  const blank = !label.trim();
  return (
    <Modal.Form
      open
      title={`Rename ${tag.code}`}
      subtitle="The code stays, so the accounts mapped to it and the formats that place it keep it."
      submitLabel="Rename"
      busy={rename.isPending}
      error={rename.error ? (rename.error as Error).message : null}
      onClose={onClose}
      onSubmit={() => {
        if (!blank)
          rename.mutate({ framework, id: tag.id!, label: label.trim() }, { onSuccess: onClose });
      }}
    >
      <InputText
        required
        label="Name"
        value={label}
        errorText={blank ? "Give the tag a name." : undefined}
        onChange={(event) => setLabel(event.target.value)}
      />
    </Modal.Form>
  );
}

/** Archives a company tag: refused while an account is mapped to it or a format places it. */
function ArchiveTagDialog({
  framework,
  tag,
  onClose,
}: {
  framework: string;
  tag: ReportTag;
  onClose: () => void;
}) {
  const archive = useArchiveReportTag();
  return (
    <Modal.Form
      open
      title={`Archive ${tag.code}?`}
      subtitle="It won't be offered for mapping or on a format again. Final statements that name it keep it."
      submitLabel="Archive"
      busy={archive.isPending}
      error={archive.error ? (archive.error as Error).message : null}
      onClose={onClose}
      onSubmit={() => archive.mutate({ framework, id: tag.id! }, { onSuccess: onClose })}
    >
      <p className="text-fg m-0 text-sm">
        An account mapped to it, or a format of yours with a line for it, has to move first.
      </p>
    </Modal.Form>
  );
}

type Dialog =
  | { kind: "add" }
  | { kind: "rename"; tag: ReportTag }
  | { kind: "archive"; tag: ReportTag }
  | null;

/**
 * The tag dictionary (ADR-0061, design plan E): each supplied framework's tags, and the company's own under them
 * (ADR-0065). An account maps to one on Account mapping, and a format's lines are made of them. Adding, renaming and
 * archiving a company tag take the formats' permission.
 */
export function ReportTagsTab() {
  const subscription = useSubscriptionStatus();
  const features = subscription.data?.features;
  const entitled = !features || features.includes(FEATURE);
  const { data: frameworks, isPending, error } = useReportFrameworks(entitled);
  const [picked, setPicked] = useState<string | null>(null);
  const [dialog, setDialog] = useState<Dialog>(null);
  const can = useCan();
  const editable = can("reports.layout.edit");

  if (!entitled) {
    return (
      <EmptyState
        icon="Lock"
        title="Report tags come with the Reporting layer add-on"
        description="Statements in the standard formats stay available. An owner or admin can add it to the subscription."
      />
    );
  }
  if (error) {
    return (
      <Alert
        config={{ variant: "subtle", colorScheme: "danger" }}
        title="The report tags couldn't be loaded"
        description={(error as Error).message}
      />
    );
  }

  const framework = frameworks?.find((f) => f.code === picked) ?? frameworks?.[0];
  const tags = framework?.tags ?? [];
  const own = tags.filter((tag) => tag.parentCode && !tag.archived).length;
  const shown = columns(
    tags.some((tag) => tag.category),
    editable ? (tag) => setDialog({ kind: "rename", tag }) : null,
    editable ? (tag) => setDialog({ kind: "archive", tag }) : null,
  );

  return (
    <div className="flex flex-col gap-4">
      <div className="flex flex-wrap items-center justify-between gap-2">
        {frameworks && frameworks.length > 1 ? (
          <SegmentControl
            label="Framework"
            segments={frameworks.map((f) => ({ value: f.code, label: f.name }))}
            value={framework?.code ?? ""}
            onChange={setPicked}
          />
        ) : (
          <span />
        )}
        {editable && framework ? (
          <Button
            variant="secondary"
            size="sm"
            leftIcon="Plus"
            onClick={() => setDialog({ kind: "add" })}
          >
            Add tag
          </Button>
        ) : null}
      </div>
      <SectionCard
        title={
          framework
            ? `${framework.name} · ${tags.length - tags.filter((t) => t.parentCode).length} supplied${own ? `, ${own} yours` : ""}`
            : "Report tags"
        }
        flush
      >
        <DataTable
          // RDS records the column order when the table mounts, so a framework with another set of columns gets its own.
          key={`${framework?.code ?? ""}-${tags.some((tag) => tag.category)}`}
          aria-label="Report tags"
          size="sm"
          enableSorting={false}
          columns={shown}
          data={tags}
          loading={isPending}
          getRowId={(row) => row.id ?? row.code}
          pagination={{ mode: "manual", show: false }}
          containerClassName="rounded-none border-0"
        />
      </SectionCard>
      <p className="text-fg-muted m-0 text-sm">
        The supplied tags come with the framework and can't be changed. A tag of your own refines
        one of them: a supplied format adds it up on its parent's line, and a format of yours can
        give it a line of its own. Each account is given one on{" "}
        <Link to="/reports/mapping" className="text-primary underline">
          Account mapping
        </Link>
        .
      </p>
      <p className="text-fg-muted m-0 text-xs">Supplied format · not a compliance assertion</p>

      {framework && dialog?.kind === "add" ? (
        <AddTagDialog framework={framework} onClose={() => setDialog(null)} />
      ) : null}
      {framework && dialog?.kind === "rename" ? (
        <RenameTagDialog
          framework={framework.code}
          tag={dialog.tag}
          onClose={() => setDialog(null)}
        />
      ) : null}
      {framework && dialog?.kind === "archive" ? (
        <ArchiveTagDialog
          framework={framework.code}
          tag={dialog.tag}
          onClose={() => setDialog(null)}
        />
      ) : null}
    </div>
  );
}
